/**
 * Color utility for converting hex values to basic color names and
 * mapping those to ChalkySticks trainer theme names.
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package Utility
 * @project ChalkySticks Web v3
 */

/**
 * RGB color components
 */
interface IRgb {
	b: number;
	g: number;
	r: number;
}

/**
 * HSL color components
 */
interface IHsl {
	h: number;
	l: number;
	s: number;
}

/**
 * Reference colors used for nearest-match comparison.
 * Each entry defines a basic color name with its representative
 * hue, saturation, and lightness values.
 */
const BASIC_COLORS: Record<string, IHsl> = {
	black: {
		h: 0,
		l: 10,
		s: 0,
	},
	blue: {
		h: 220,
		l: 50,
		s: 80,
	},
	green: {
		h: 120,
		l: 40,
		s: 60,
	},
	grey: {
		h: 0,
		l: 50,
		s: 0,
	},
	orange: {
		h: 30,
		l: 50,
		s: 90,
	},
	purple: {
		h: 280,
		l: 50,
		s: 80,
	},
	red: {
		h: 0,
		l: 45,
		s: 80,
	},
	sky: {
		h: 195,
		l: 55,
		s: 70,
	},
	teal: {
		h: 175,
		l: 40,
		s: 60,
	},
	white: {
		h: 0,
		l: 95,
		s: 0,
	},
	yellow: {
		h: 55,
		l: 55,
		s: 90,
	},
};

/**
 * Maps basic color names to trainer theme names.
 */
const COLOR_TO_THEME: Record<string, string> = {
	black: 'Charcoal',
	blue: 'Classic',
	green: 'Traditional',
	grey: 'Matchroom',
	orange: 'Yellow',
	purple: 'Purple',
	red: 'Superfine',
	sky: 'Chalky',
	teal: 'TapAKeg',
	white: 'Matchroom',
	yellow: 'Yellow',
};

/**
 * Parse a hex color string into its RGB components.
 *
 * @param string hex
 * @return IRgb
 */
export function hexToRgb(hex: string): IRgb {
	// Strip leading hash if present
	const cleaned = hex.replace(/^#/, '');
	const short = cleaned
		.split('')
		.map((character) => character + character)
		.join('');

	// Expand shorthand (e.g. "abc" -> "aabbcc")
	const full = cleaned.length === 3 ? short : cleaned;

	const value = parseInt(full, 16);

	return {
		b: value & 0xff,
		g: (value >> 8) & 0xff,
		r: (value >> 16) & 0xff,
	};
}

/**
 * Convert RGB color components to HSL. Returns hue in degrees (0-360),
 * saturation and lightness as percentages (0-100).
 *
 * @param number r
 * @param number g
 * @param number b
 * @return IHsl
 */
export function rgbToHsl(r: number, g: number, b: number): IHsl {
	const rNormalized = r / 255;
	const gNormalized = g / 255;
	const bNormalized = b / 255;

	const max = Math.max(rNormalized, gNormalized, bNormalized);
	const min = Math.min(rNormalized, gNormalized, bNormalized);
	const delta = max - min;

	let hue = 0;
	let saturation = 0;
	const lightness = (max + min) / 2;

	if (delta !== 0) {
		saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

		if (max === rNormalized) {
			hue = ((gNormalized - bNormalized) / delta + (gNormalized < bNormalized ? 6 : 0)) * 60;
		} else if (max === gNormalized) {
			hue = ((bNormalized - rNormalized) / delta + 2) * 60;
		} else {
			hue = ((rNormalized - gNormalized) / delta + 4) * 60;
		}
	}

	return {
		h: Math.round(hue),
		l: Math.round(lightness * 100),
		s: Math.round(saturation * 100),
	};
}

/**
 * Find the nearest basic color name for a given hex value.
 * Uses HSL color space for perceptually accurate matching.
 * Achromatic colors (low saturation) are matched by lightness,
 * while chromatic colors are matched primarily by hue.
 *
 * @param string hex
 * @return string
 */
export function nearestBasicColor(hex: string): string {
	const rgb = hexToRgb(hex);
	const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

	// Achromatic threshold: if saturation is very low, match by lightness
	if (hsl.s < 15) {
		if (hsl.l < 20) {
			return 'black';
		} else if (hsl.l > 85) {
			return 'white';
		}

		return 'grey';
	}

	// Chromatic: find the closest basic color by weighted HSL distance
	let bestColor = 'grey';
	let bestDistance = Infinity;

	for (const [name, reference] of Object.entries(BASIC_COLORS)) {
		// Skip achromatic reference colors for chromatic input
		if (reference.s === 0) {
			continue;
		}

		// Hue wraps around at 360 degrees, so we need circular distance
		const hueDifference = Math.abs(hsl.h - reference.h);
		const hueDistance = Math.min(hueDifference, 360 - hueDifference);

		// Weight hue more heavily than saturation/lightness
		const distance = hueDistance * 2 + Math.abs(hsl.s - reference.s) + Math.abs(hsl.l - reference.l);

		if (distance < bestDistance) {
			bestDistance = distance;
			bestColor = name;
		}
	}

	return bestColor;
}

/**
 * Convert a hex color to the best matching ChalkySticks trainer
 * theme name. Falls back to 'Chalky' if no match is found.
 *
 * @param string hex
 * @return string
 */
export function hexToTrainerTheme(hex: string): string {
	const colorName = nearestBasicColor(hex);

	return COLOR_TO_THEME[colorName] || 'Chalky';
}
